import { motion } from 'framer-motion';
import { Calendar, CheckCircle, Clock, Mail, Phone, User } from 'lucide-react';
import React from 'react';
import ImageWithPlaceholder from '../../../shared/components/ImageWithPlaceholderProps';
import { Provider } from '../../../provider/types';

// Define default colors to use if not provided
const defaultColors = {
  cardBackground: 'rgba(0, 0, 0, 0.95)',
  cardBorder: '#DDB88919',
  cardBorderSelected: "#22c55e49",
  cardShadow: 'rgba(221,184,137,0.07)',
  cardShadowSelected: "rgba(34,197,94,0.25)",
  titleText: '#DDB889',
  subtitleText: '#9CA3AF',
  iconColor: '#DDB889B2',
  selectionBackground: "#22c55e",
  selectionIcon: "#FFFFFF"
};

interface ProviderListProps {
  providers: { node: Provider }[];
  isClickable?: boolean;
  onProviderClick?: (provider: Provider) => void;
  selectedProviders?: Provider | Provider[] | null;
  showPhotos?: boolean;
  showAvailability?: boolean;
  colors?: {
    cardBackground?: string;
    cardBorder?: string;
    cardBorderSelected?: string;
    placeholderGradientFrom?: string;
    placeholderIconBackground?: string;
    titleText?: string;
    subtitleText?: string;
    infoText?: string;
    iconColor?: string;
    selectionBackground?: string;
    selectionIcon?: string;
  };
}

const ProviderList: React.FC<ProviderListProps> = ({
  providers,
  isClickable = false,
  onProviderClick,
  selectedProviders = null,
  showPhotos = true,
  showAvailability = true,
  colors = {}
}) => {
  // Merge provided colors with defaults
  const themeColors = { ...defaultColors, ...colors };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3
      }
    }
  };

  const isProviderSelected = (provider: Provider) => {
    if (!selectedProviders) return false;
    if (Array.isArray(selectedProviders)) {
      return selectedProviders.some(selected => selected.id === provider.id);
    }
    return selectedProviders.id === provider.id;
  };

  const handleProviderClick = (provider: Provider) => {
    if (!isClickable || !onProviderClick) return;
    onProviderClick(provider);
  };

  const capitalizeFirstLetter = (string: string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="lp-flex lp-flex-col lp-gap-3 lp-w-full"
    >
      {providers.map(({ node: provider }) => {
        const isSelected = isProviderSelected(provider);

        return (
          <motion.div
  key={provider.id}
  variants={itemVariants}
  className={`
    lp-rounded-xl lp-overflow-hidden lp-border lp-transition-all lp-duration-300
    ${isClickable ? 'lp-cursor-pointer' : ''}
  `}
  style={{
    backgroundColor: themeColors.cardBackground,
    borderColor: isSelected ? themeColors.cardBorderSelected : themeColors.cardBorder,
    boxShadow: isSelected
      ? `0 0 15px ${themeColors.cardShadowSelected}`
      : `0 0 10px ${themeColors.cardShadow}`
  }}
          >
            <div
              className="lp-p-3 lp-transition-colors lp-duration-200 lp-border-b"
              style={{
                backgroundColor: isSelected
                  ? themeColors.placeholderGradientFrom || `${themeColors.titleText}19`
                  : 'transparent',
                borderBottomColor: `${themeColors.cardBorder}5D` || `${themeColors.titleText}0D`
              }}
            >
              <div className="lp-flex lp-items-center lp-gap-3">
                {/* Provider avatar or image */}
                {showPhotos && (
                  provider.image ? (
                    <div className="lp-w-12 lp-h-12 lp-rounded-full lp-overflow-hidden lp-shrink-0 lp-border"
                      style={{
                        borderColor: isSelected ? themeColors.cardBorderSelected : themeColors.cardBorder
                      }}
                    >
                      <ImageWithPlaceholder
                        image={provider.image}
                        alt={`${provider.firstName} ${provider.lastName}`}
                        className="lp-w-full lp-h-full lp-object-cover"
                      />
                    </div>
                  ) : (
                    <div
                      className="lp-w-12 lp-h-12 lp-rounded-full lp-flex lp-items-center lp-justify-center lp-shrink-0 lp-border"
                      style={{
                        backgroundColor: isSelected
                          ? themeColors.placeholderIconBackground || `${themeColors.titleText}33`
                          : themeColors.placeholderGradientFrom || `${themeColors.titleText}19`,
                        borderColor: isSelected ? themeColors.cardBorderSelected : themeColors.cardBorder
                      }}
                    >
                      {isSelected ? (
                        <CheckCircle
                          className="lp-w-6 lp-h-6"
                          style={{ color: themeColors.titleText }}
                        />
                      ) : (
                        <User
                          className="lp-w-6 lp-h-6"
                          style={{ color: themeColors.iconColor || `${themeColors.titleText}B3` }}
                        />
                      )}
                    </div>
                  )
                )}

                {/* Provider information */}
                <div className="lp-flex-grow lp-min-w-0">
                  <h4
                    className="lp-font-medium"
                    style={{ color: themeColors.titleText }}
                  >
                    {capitalizeFirstLetter(provider.firstName)} {capitalizeFirstLetter(provider.lastName)}
                  </h4>

                  {provider.title && (
                    <p
                      className="lp-text-xs lp-mt-0.5"
                      style={{ color: themeColors.subtitleText || themeColors.iconColor }}
                    >
                      {provider.title}
                    </p>
                  )}

                  <div className="lp-flex lp-flex-wrap lp-gap-4 lp-mt-1">
                    <div className="lp-flex lp-items-center lp-gap-1 lp-text-xs">
                      <Phone
                        className="lp-w-3 lp-h-3"
                        style={{ color: themeColors.iconColor || `${themeColors.titleText}B3` }}
                      />
                      <span style={{ color: themeColors.iconColor || `${themeColors.titleText}B3` }}>
                        {provider.phone}
                      </span>
                    </div>

                    <div className="lp-flex lp-items-center lp-gap-1 lp-text-xs">
                      <Mail
                        className="lp-w-3 lp-h-3"
                        style={{ color: themeColors.iconColor || `${themeColors.titleText}B3` }}
                      />
                      <span style={{ color: themeColors.iconColor || `${themeColors.titleText}B3` }}>
                        {provider.email}
                      </span>
                    </div>

                    {/* Only show availability info if showAvailability is true */}
                    {showAvailability && provider.availability && (
                      <>
                        <div className="lp-flex lp-items-center lp-gap-1 lp-text-xs">
                          <Calendar
                            className="lp-w-3 lp-h-3"
                            style={{ color: themeColors.iconColor || `${themeColors.titleText}B3` }}
                          />
                          <span style={{ color: themeColors.iconColor || `${themeColors.titleText}B3` }}>
                            {provider.availability.days || "Not specified"}
                          </span>
                        </div>

                        <div className="lp-flex lp-items-center lp-gap-1 lp-text-xs">
                          <Clock
                            className="lp-w-3 lp-h-3"
                            style={{ color: themeColors.iconColor || `${themeColors.titleText}B3` }}
                          />
                          <span style={{ color: themeColors.iconColor || `${themeColors.titleText}B3` }}>
                            {provider.availability.hours || "Not specified"}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        );
      })}
    </motion.div>
  );
};

export default ProviderList;
